import type {DialogPanelMode} from './modal-utils';
import type {CSSProperties, ReactNode} from 'react';

import {createPortal} from 'react-dom';

import {cn} from '../../helpers/cn';
import {splitModalContentStyle} from './modal-utils';
import {ModalHeader} from './ModalHeader';
import {useAnimatedDialog} from './useAnimatedDialog';

export type AnimatedDialogProps = {
  show: boolean;
  onClose?: () => void;
  canceledOnTouchOutside?: boolean;
  canceledOnTouchInside?: boolean;
  children?: ReactNode;
  contentStyle?: CSSProperties;
  headerStyle?: CSSProperties;
  footerStyle?: CSSProperties;
  header?: ReactNode;
  footer?: ReactNode | null;
  defaultHeader?: boolean;
  showCloseButton?: boolean;
  title?: string;
  panelMode: DialogPanelMode;
  className?: string;
  /** Applied when panelMode is bottom and no explicit height/maxHeight is set */
  defaultBottomMaxHeight?: string;
};

export function AnimatedDialog({
  show,
  onClose,
  canceledOnTouchOutside = true,
  canceledOnTouchInside = false,
  children,
  contentStyle = {},
  headerStyle = {},
  footerStyle = {},
  header,
  footer = null,
  defaultHeader = false,
  showCloseButton = true,
  title = '',
  panelMode,
  className,
  defaultBottomMaxHeight = '90dvh'
}: AnimatedDialogProps) {
  const {dialogRef, titleId, isMounted, isPanelOpen, isInteractive, handlePanelTransitionEnd} =
    useAnimatedDialog(show, onClose);

  if (!isMounted) {
    return null;
  }

  const {panelStyle: contentPanelStyle, bodyStyle} = splitModalContentStyle(contentStyle);
  const hasHeader = defaultHeader || Boolean(header);
  const hasFooter = Boolean(footer);
  const isBottom = panelMode === 'bottom';
  const isFullscreen = panelMode === 'fullscreen';

  const panelStyle: CSSProperties = {
    ...contentPanelStyle,
    ...(isBottom &&
    !isFullscreen &&
    contentPanelStyle.maxHeight == null &&
    contentPanelStyle.height == null
      ? {maxHeight: defaultBottomMaxHeight}
      : {})
  };

  const modal = (
    <div
      className={cn(
        'ds-modal-mask pointer-events-auto fixed inset-0 z-modal mx-auto flex max-w-mobile items-center justify-center bg-black/50',
        isPanelOpen ? 'ds-modal-mask--open' : 'ds-modal-mask--leave',
        isBottom && 'items-end',
        className
      )}
      onClick={isInteractive && canceledOnTouchOutside ? onClose : undefined}
      role="presentation"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'ds-modal-panel flex flex-col overflow-hidden rounded-lg bg-white outline-none',
          `ds-modal-panel--${panelMode}`,
          isPanelOpen ? 'ds-modal-panel--open' : 'ds-modal-panel--leave',
          isFullscreen && 'h-full w-full rounded-none',
          !isFullscreen && !isBottom && 'w-[85%]',
          isBottom && 'w-full rounded-b-none'
        )}
        onTransitionEnd={handlePanelTransitionEnd}
        onClick={
          canceledOnTouchInside
            ? undefined
            : (event) => {
                event.stopPropagation();
              }
        }
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={defaultHeader && title ? titleId : undefined}
        aria-label={!defaultHeader || !title ? title || 'مودال' : undefined}
      >
        {hasHeader ? (
          <div
            className="shrink-0 bg-white"
            style={headerStyle}
            onClick={(event) => event.stopPropagation()}
          >
            {defaultHeader ? (
              <ModalHeader
                title={title}
                titleId={titleId}
                onClose={onClose}
                showCloseButton={showCloseButton}
              />
            ) : (
              header
            )}
          </div>
        ) : null}
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          style={bodyStyle}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
        {hasFooter ? (
          <div
            className="shrink-0 bg-white"
            style={footerStyle}
            onClick={(event) => event.stopPropagation()}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
